import { hsl, slurp, experp, rgb, easeInOut, clampedSlurp } from "./util";
import { Circle } from "./circle";

const MINUTES = 60;

const COLORS = [
	{r: 69, g: 74, b: 222},
	{r: 206, g: 45, b: 79},
	{r: 255, g: 190, b: 11},
	{r: 69, g: 74, b: 222}, // loop around to be lazy
];

export default class Controller {

	constructor(canvasSelector) {
		this.canvas = document.querySelector(canvasSelector);
		/** @type {!CanvasRenderingContext2D} */
		this.context = this.canvas.getContext('2d');

		this.width = 100;
		this.height = 100;
		this.scale = 1;

		this.colorAnimAmt = Math.random();
		this.colorPeriod = 3 * MINUTES;
		this.baseColor = 'grey',
		this.highlightColor = 'white';

		[this.baseColor, this.highlightColor] = getColor(this.colorAnimAmt);

		this.animAmt = Math.random();
		this.period = 60;

		this.updateCssAmt = 1; // start at 1 -> update the CSS straight away
		this.updateCssPeriod = 0.5;

		this.currentOriginPoint = this.originPoint;

		this.lastMousePoint = {x: 0, y: 0};
		this.mouseAmt = 0;
		this.mouseFallbackTime = 5;

		this.circles = [];
	}

	get originAmt() {
		return {
			x: 0.5 + 0.3 * Math.cos(2 * Math.PI * this.animAmt),
			y: 0.5 + 0.3 * Math.sin(3 * 2 * Math.PI * this.animAmt),
		}
	}

	get originPoint() {
		const originAmt = this.originAmt;
		return {
			x: this.width * originAmt.x,
			y: this.height * originAmt.y,
		}
	}

	// Combination of the mouse position and the origin point.
	get adjustedOriginPoint() {
		// just doing this for the mo
		const originPoint = this.originPoint;
		const adjustedMouseAmt = easeInOut(this.mouseAmt, 3);
		return {
			x: slurp(originPoint.x, this.lastMousePoint.x, adjustedMouseAmt),
			y: slurp(originPoint.y, this.lastMousePoint.y, adjustedMouseAmt),
		}
	}

	update(dt) {
		this.addAllCircles();

		this.animAmt += dt / this.period;
		this.animAmt %= 1;

		this.colorAnimAmt += dt / this.colorPeriod;
		this.colorAnimAmt %= 1;
		[this.baseColor, this.highlightColor] = getColor(this.colorAnimAmt);

		this.updateCssAmt += dt / this.updateCssPeriod;

		this.mouseAmt -= dt / this.mouseFallbackTime;
		if (this.mouseAmt < 0) {
			this.mouseAmt = 0;
		}

		const originPoint = this.adjustedOriginPoint;
		this.currentOriginPoint = {
			x: slurp(this.currentOriginPoint.x, originPoint.x, 0.1),
			y: slurp(this.currentOriginPoint.y, originPoint.y, 0.1),
		}
	}

	clear() {
		// Clear the previous frame
		this.context.resetTransform();
		this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

		this.context.scale(this.scale, this.scale);
	}

	/**
	 * @param {CanvasRenderingContext2D} context 
	 */
	render() {
		const originPoint = this.currentOriginPoint;

		this.context.fillStyle = this.baseColor;
		this.context.fillRect(0, 0, this.width, this.height);
		for (const circle of this.circles) {
			const sqDistToOrigin = circle.sqDistToPoint(originPoint);
			const distAmt = 1 / sqDistToOrigin;
			const points = clampedSlurp(3, 50, 500 * distAmt * circle.radius);
	
			this.context.beginPath();
			this.context.strokeStyle = this.highlightColor;
			this.context.lineWidth = 2;
			this.drawShape(this.transformPoints(circle.getPoints(points)));
			this.context.stroke();
		}

		if (this.updateCssAmt >= 1) {
			this.updateCssAmt = 0;
			// I guess also update the CSS here?
			document.body.style.setProperty('--base-color', this.baseColor);
		}
	}

	resize() {
		let pixelRatio = window.devicePixelRatio || 1;

		this.canvas.width = this.canvas.clientWidth * pixelRatio;
		this.canvas.height = this.canvas.clientHeight * pixelRatio;
		this.scale = pixelRatio;
		this.width = this.canvas.clientWidth;
		this.height = this.canvas.clientHeight;
	
		this.render();
	}

	onMouseInput(mousePoint) {
		if (mousePoint.y > this.height) {
			return;
		}
		this.lastMousePoint = mousePoint;
		this.mouseAmt = 1;
	}

	drawShape(points) {
		for (let i = 0; i < points.length; i++) {
			if (i == 0) {
				this.context.moveTo(points[i].x, points[i].y);
			}
			else {
				this.context.lineTo(points[i].x, points[i].y);
			}
		}
		this.context.closePath();
	}

	transformPoints(points) {
		const originPoint = this.currentOriginPoint;
		return points.map(p => {
			const x = p.x - originPoint.x;
			const y = p.y - originPoint.y;
			const r = x * x + y * y;
			const scaleThing = 0.00006;
			return {
				x: x / (scaleThing * r) + originPoint.x,
				y: y / (scaleThing * r) + originPoint.y,
			}
		});
	}

	addAllCircles() {
		while (this.circles.length < 400) {
			this.addCircle();
		}
	}

	addCircle() {
		const circle = new Circle();
		circle.radius = experp(30, 70, Math.random());
		while (true) {
			circle.x = slurp(-1, 2, Math.random()) * this.canvas.width;
			circle.y = slurp(-1, 2, Math.random()) * this.canvas.height;

			let touching = false;
			for (const other of this.circles) {
				if (circle.touching(other)) {
					touching = true;
				}
			}

			if (touching) {
				circle.radius *= 0.9;
				continue;
			}

			break;
		}

		this.circles.push(circle);
	}
}

function getColor(amt) {
	const colorIndex = Math.floor(3 * amt);
	const colorAmt = easeInOut((3 * amt) % 1, 5);

	const colorComponents = {
		r: slurp(COLORS[colorIndex].r, COLORS[colorIndex+1].r, colorAmt),
		g: slurp(COLORS[colorIndex].g, COLORS[colorIndex+1].g, colorAmt),
		b: slurp(COLORS[colorIndex].b, COLORS[colorIndex+1].b, colorAmt),
	};

	const baseColor = rgb(
		0.8 * colorComponents.r,
		0.8 * colorComponents.g,
		0.8 * colorComponents.b
	);
	const highlightColor = rgb(
		colorComponents.r,
		colorComponents.g,
		colorComponents.b
	);
	return [baseColor, highlightColor]
}


// WEBPACK FOOTER //
// ./src/js/controller.js